import type { CSSProperties } from 'react';

export type ProtectiveMode = 'off' | 'percent' | 'price';
export type TrailingMode = 'off' | 'on';

export type ProtectiveState = {
  tpMode: ProtectiveMode;
  tpValue: string;
  slMode: ProtectiveMode;
  slValue: string;
  trailMode: TrailingMode;
  /** Callback % for trailing (e.g. 1 = 1%). */
  trailCallbackPct: string;
  trailActivate: string;
};

export const defaultProtective = (): ProtectiveState => ({
  tpMode: 'percent',
  tpValue: '1.5',
  slMode: 'percent',
  slValue: '1',
  trailMode: 'off',
  trailCallbackPct: '1',
  trailActivate: '',
});

/** Resolve TP/SL trigger prices from entry + side. */
export const resolveProtectivePrices = (
  entry: number,
  side: 'buy' | 'sell',
  p: ProtectiveState,
): { tp?: number; sl?: number; trailCallback?: number; trailActivate?: number } => {
  if (!Number.isFinite(entry) || entry <= 0) return {};
  const long = side === 'buy';
  const out: { tp?: number; sl?: number; trailCallback?: number; trailActivate?: number } = {};

  if (p.tpMode === 'price') {
    const v = Number(p.tpValue);
    if (Number.isFinite(v) && v > 0) out.tp = v;
  } else if (p.tpMode === 'percent') {
    const pct = Number(p.tpValue);
    if (Number.isFinite(pct) && pct > 0) {
      out.tp = long ? entry * (1 + pct / 100) : entry * (1 - pct / 100);
    }
  }

  if (p.slMode === 'price') {
    const v = Number(p.slValue);
    if (Number.isFinite(v) && v > 0) out.sl = v;
  } else if (p.slMode === 'percent') {
    const pct = Number(p.slValue);
    if (Number.isFinite(pct) && pct > 0) {
      out.sl = long ? entry * (1 - pct / 100) : entry * (1 + pct / 100);
    }
  }

  if (p.trailMode === 'on') {
    const cb = Number(p.trailCallbackPct);
    if (Number.isFinite(cb) && cb > 0) out.trailCallback = Math.min(99.99, Math.max(0.1, cb)) / 100;
    const act = Number(p.trailActivate);
    if (Number.isFinite(act) && act > 0) out.trailActivate = act;
  }

  return out;
};

export const estimatePnlUsdt = (
  entry: number,
  exit: number,
  qty: number,
  side: 'buy' | 'sell',
): number | null => {
  if (![entry, exit, qty].every((n) => Number.isFinite(n) && n > 0)) return null;
  const dir = side === 'buy' ? 1 : -1;
  return (exit - entry) * qty * dir;
};

type Props = {
  side: 'buy' | 'sell';
  entryPrice: number | null;
  qty: string;
  value: ProtectiveState;
  onChange: (next: ProtectiveState) => void;
  labels?: Partial<Record<string, string>>;
};

const fmt = (n: number | null | undefined, d = 6) => {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return n.toLocaleString('en-US', { maximumFractionDigits: d });
};

export default function ProtectiveOrders({
  side, entryPrice, qty, value, onChange, labels = {},
}: Props) {
  const L = {
    title: labels.title || 'Protective · TP / SL / Trailing',
    hint: labels.hint || 'Attached when the entry fills (reduce-only). Like WEEX bracket / plan orders.',
    tp: labels.tp || 'Take profit',
    sl: labels.sl || 'Stop loss',
    trail: labels.trail || 'Trailing',
    off: labels.off || 'Off',
    pct: labels.pct || '% from entry',
    price: labels.price || 'Price',
    callback: labels.callback || 'Callback %',
    activate: labels.activate || 'Activate at (opt.)',
    preview: labels.preview || 'Approx. from entry',
    tpPnl: labels.tpPnl || 'At TP',
    slPnl: labels.slPnl || 'At SL',
  };

  const entry = entryPrice && entryPrice > 0 ? entryPrice : null;
  const resolved = entry ? resolveProtectivePrices(entry, side, value) : {};
  const q = Number(qty);
  const tpPnl = entry && resolved.tp != null ? estimatePnlUsdt(entry, resolved.tp, q, side) : null;
  const slPnl = entry && resolved.sl != null ? estimatePnlUsdt(entry, resolved.sl, q, side) : null;

  const set = (patch: Partial<ProtectiveState>) => onChange({ ...value, ...patch });

  const modeRow = (
    key: 'tp' | 'sl',
    mode: ProtectiveMode,
    val: string,
  ) => (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        {key === 'tp' ? L.tp : L.sl}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
        {([
          ['off', L.off],
          ['percent', L.pct],
          ['price', L.price],
        ] as const).map(([id, lab]) => (
          <button
            key={id}
            type="button"
            onClick={() => set(key === 'tp' ? { tpMode: id } : { slMode: id })}
            style={{
              ...chip,
              borderColor: mode === id ? 'var(--gold)' : 'var(--line)',
              color: mode === id ? 'var(--gold)' : 'var(--text)',
            }}
          >{lab}</button>
        ))}
      </div>
      {mode !== 'off' ? (
        <input
          value={val}
          onChange={(e) => set(key === 'tp' ? { tpValue: e.target.value } : { slValue: e.target.value })}
          placeholder={mode === 'percent' ? '1.5' : String(entry ?? '')}
          style={inp}
        />
      ) : null}
      {mode !== 'off' && resolved[key] != null ? (
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)', marginTop: 4 }}>
          → {fmt(resolved[key])}
        </div>
      ) : null}
    </div>
  );

  return (
    <div style={{
      marginTop: 10, padding: 10, borderRadius: 10,
      border: '1px solid var(--line)', background: 'var(--panel-2)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{L.title}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>{L.hint}</div>

      {modeRow('tp', value.tpMode, value.tpValue)}
      {modeRow('sl', value.slMode, value.slValue)}

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>{L.trail}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <button
            type="button"
            onClick={() => set({ trailMode: 'off' })}
            style={{ ...chip, borderColor: value.trailMode === 'off' ? 'var(--gold)' : 'var(--line)', color: value.trailMode === 'off' ? 'var(--gold)' : 'var(--text)' }}
          >{L.off}</button>
          <button
            type="button"
            onClick={() => set({ trailMode: 'on' })}
            style={{ ...chip, borderColor: value.trailMode === 'on' ? 'var(--gold)' : 'var(--line)', color: value.trailMode === 'on' ? 'var(--gold)' : 'var(--text)' }}
          >Trailing mkt</button>
        </div>
        {value.trailMode === 'on' ? (
          <>
            <label style={{ display: 'block', marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>{L.callback}</label>
            <input value={value.trailCallbackPct} onChange={(e) => set({ trailCallbackPct: e.target.value })} style={inp} />
            <label style={{ display: 'block', marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>{L.activate}</label>
            <input value={value.trailActivate} onChange={(e) => set({ trailActivate: e.target.value })} placeholder={String(entry ?? '')} style={inp} />
          </>
        ) : null}
      </div>

      {entry ? (
        <div style={{
          marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--line)',
          fontSize: 11, fontFamily: 'var(--mono)', lineHeight: 1.55, color: 'var(--muted)',
        }}>
          <div style={{ color: 'var(--text)', marginBottom: 2 }}>{L.preview} · {fmt(entry)}</div>
          <div>
            {L.tpPnl}:{' '}
            <span style={{ color: (tpPnl ?? 0) >= 0 ? 'var(--long)' : 'var(--short)' }}>
              {tpPnl == null ? '—' : `${tpPnl >= 0 ? '+' : ''}${fmt(tpPnl, 2)} USDT`}
            </span>
            {resolved.tp != null ? <span style={{ color: 'var(--muted)' }}> @ {fmt(resolved.tp)}</span> : null}
          </div>
          <div>
            {L.slPnl}:{' '}
            <span style={{ color: (slPnl ?? 0) >= 0 ? 'var(--long)' : 'var(--short)' }}>
              {slPnl == null ? '—' : `${slPnl >= 0 ? '+' : ''}${fmt(slPnl, 2)} USDT`}
            </span>
            {resolved.sl != null ? <span style={{ color: 'var(--muted)' }}> @ {fmt(resolved.sl)}</span> : null}
          </div>
          {value.trailMode === 'on' ? (
            <div>Trail callback {value.trailCallbackPct || '—'}% · activate {value.trailActivate || 'market'}</div>
          ) : null}
        </div>
      ) : (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>
          Entry price needed for % / PnL preview (limit/stop price or last).
        </div>
      )}
    </div>
  );
}

const chip: CSSProperties = {
  padding: '6px 4px', borderRadius: 8, border: '1px solid var(--line)',
  background: 'var(--panel)', cursor: 'pointer', fontSize: 11,
};
const inp: CSSProperties = {
  width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--text)',
};
